import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Snackbar,
  Paper,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import { NumericFormat } from 'react-number-format';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { canManageShowcase } from '../tenancy/resourcePermissions';

function ShowcasePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showcaseData, setShowcaseData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const [slugStatus, setSlugStatus] = useState({ isChecking: false, isUnique: true, message: '' });
  const [slugDebounceTimer, setSlugDebounceTimer] = useState(null);
  const [isShowcaseNotFound, setIsShowcaseNotFound] = useState(false);
  const [showcasePermissions, setShowcasePermissions] = useState(null);

  useEffect(() => {
    fetchShowcaseData();
  }, []);

  const fetchShowcaseData = async () => {
    try {
      setLoading(true);
      setIsShowcaseNotFound(false);
      const response = await apiClient.get('/user-showcase');
      setShowcaseData(response.data);
      setShowcasePermissions(response.data?.permissions ?? null);
      setEditData(response.data);
      logger.log('Данные витрины:', response.data);
    } catch (error) {
      logger.error('Ошибка при загрузке данных витрины:', error);
      if (error.response && error.response.status === 404) {
        setIsShowcaseNotFound(true);
        setShowcasePermissions(error.response.data?.permissions ?? null);
        setEditData({
          title: '',
          description: '',
          slug: '',
          contactEmail: '',
          contactPhone: '',
          is_active: true,
        });
      } else {
        setError('Не удалось загрузить данные витрины');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (!canManageShowcase(showcaseData || { permissions: showcasePermissions })) return;
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleStartCreate = () => {
    if (!canManageShowcase(showcaseData || { permissions: showcasePermissions })) return;
    setIsCreating(true);
    setIsEditing(false);
    setEditData({
      title: '',
      description: '',
      slug: '',
      contactEmail: '',
      contactPhone: '',
      is_active: true,
    });
    setSlugStatus({ isChecking: false, isUnique: true, message: '' });
  };

  const handleCancel = () => {
    if (isCreating) {
      setIsCreating(false);
    } else {
      setEditData(showcaseData);
      setIsEditing(false);
    }
    setSlugStatus({ isChecking: false, isUnique: true, message: '' });
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === 'is_active') {
      setEditData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setEditData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === 'slug') {
      setSlugStatus({ isChecking: false, isUnique: true, message: '' });
    }
  };

  const handleSlugBlur = async (e) => {
    const { value } = e.target;

    if (!value) return;

    if (!isCreating && value === showcaseData?.slug) return;

    const slugRegex = /^[a-z0-9-]+$/;
    if (!slugRegex.test(value)) {
      setSlugStatus({
        isChecking: false,
        isUnique: false,
        message: 'Slug может содержать только строчные латинские буквы, цифры и дефисы',
      });
      return;
    }

    if (slugDebounceTimer) {
      clearTimeout(slugDebounceTimer);
    }

    setSlugStatus({ isChecking: true, isUnique: true, message: 'Проверка...' });

    const timer = setTimeout(async () => {
      try {
        const response = await apiClient.get('/check-slug', { params: { slug: value } });

        if (response.data.isUnique) {
          setSlugStatus({
            isChecking: false,
            isUnique: true,
            message: 'Slug доступен',
          });
        } else {
          setSlugStatus({
            isChecking: false,
            isUnique: false,
            message: 'Этот slug уже используется',
          });
        }
      } catch (error) {
        logger.error('Ошибка при проверке уникальности slug:', error);
        setSlugStatus({
          isChecking: false,
          isUnique: false,
          message: 'Ошибка при проверке slug',
        });
      }
    }, 500);

    setSlugDebounceTimer(timer);
  };

  const formatSlug = (value) => {
    if (!value) return '';

    let formatted = value.toLowerCase();

    formatted = formatted.replace(/\s+/g, '-');

    formatted = formatted.replace(/[^a-z0-9-]/g, '');

    formatted = formatted.replace(/-+/g, '-');

    return formatted;
  };

  const handleSlugChange = (e) => {
    const { value } = e.target;
    const formattedValue = formatSlug(value);

    setEditData((prev) => ({ ...prev, slug: formattedValue }));
  };

  const handleCreate = async () => {
    if (!canManageShowcase(showcaseData || { permissions: showcasePermissions })) return;
    try {
      if (!editData.title || !editData.slug) {
        setNotification({
          open: true,
          message: 'Название и URL витрины обязательны для заполнения',
          type: 'error',
        });
        return;
      }
      
      if (!slugStatus.isUnique) {
        setNotification({ 
          open: true, 
          message: 'Исправьте ошибки в slug перед созданием', 
          type: 'error',
        });
        return;
      }
      
      setSaveLoading(true);
      
      const response = await apiClient.post('/user-showcase', editData);
      
      setShowcaseData(response.data);
      setShowcasePermissions(response.data?.permissions ?? null);
      setEditData(response.data);
      setIsCreating(false);
      setIsShowcaseNotFound(false);
      setSlugStatus({ isChecking: false, isUnique: true, message: '' });
      setNotification({ 
        open: true, 
        message: 'Витрина успешно создана', 
        type: 'success',
      });
      
      logger.log('Витрина создана:', response.data);
    } catch (error) {
      logger.error('Ошибка при создании витрины:', error);
      setNotification({ 
        open: true, 
        message: error.response?.data?.error || 'Ошибка при создании витрины', 
        type: 'error',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canManageShowcase(showcaseData || { permissions: showcasePermissions })) return;
    try {
      if (!slugStatus.isUnique) {
        setNotification({ 
          open: true, 
          message: 'Исправьте ошибки в slug перед сохранением', 
          type: 'error',
        });
        return;
      }
      
      setSaveLoading(true);
      
      const response = await apiClient.put('/user-showcase', editData);
      
      setShowcaseData(response.data);
      setShowcasePermissions(response.data?.permissions ?? null);
      setEditData(response.data);
      setIsEditing(false);
      setSlugStatus({ isChecking: false, isUnique: true, message: '' });
      setNotification({ 
        open: true, 
        message: 'Данные витрины успешно обновлены', 
        type: 'success',
      });
      
      logger.log('Данные витрины обновлены:', response.data);
    } catch (error) {
      logger.error('Ошибка при обновлении данных витрины:', error);
      setNotification({ 
        open: true, 
        message: error.response?.data?.error || 'Ошибка при обновлении данных витрины', 
        type: 'error',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';

    const cleaned = ('' + phoneNumber).replace(/\D/g, '');

    if (cleaned.length === 11) {
      return `+${cleaned.substring(0, 1)} (${cleaned.substring(1, 4)}) ${cleaned.substring(
        4,
        7,
      )}-${cleaned.substring(7, 9)}-${cleaned.substring(9, 11)}`;
    }

    return phoneNumber;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !isShowcaseNotFound) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const baseShowcaseUrl = import.meta.env.VITE_OPEN_APP_URL + '/showcase/';

  const renderForm = () => (
    <Paper elevation={3}>
      <Box p={3}>
        <Typography variant="h6" mb={2}>
          {isCreating ? 'Создание новой витрины' : 'Редактирование витрины'}
        </Typography>
        
        <TextField
          fullWidth
          margin="normal"
          label="Название витрины"
          placeholder='Например: "Витрина агента Петра Иванова"'
          name="title"
          value={editData?.title || ''}
          onChange={handleChange}
          required
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="Описание"
          placeholder='Например: "Агентство недвижимости Стандарт РИЕЛТ г. Москва"'
          name="description"
          multiline
          rows={4}
          value={editData?.description || ''}
          onChange={handleChange}
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="URL-адрес витрины"
          name="slug"
          value={editData?.slug || ''}
          onChange={handleSlugChange}
          onBlur={handleSlugBlur}
          placeholder="например: ivanov-agent"
          helperText={
            slugStatus.message || 'Используйте только латинские буквы, цифры и дефисы'
          }
          error={!slugStatus.isUnique}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {baseShowcaseUrl}
                  </Typography>
                </Box>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {slugStatus.isChecking ? (
                  <CircularProgress size={20} />
                ) : slugStatus.message ? (
                  <Tooltip title={slugStatus.message}>
                    {slugStatus.isUnique ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </Tooltip>
                ) : (
                  <Tooltip title="URL-адрес вашей персональной витрины">
                    <InfoIcon color="action" />
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="Контактная электронная почта"
          placeholder="(необязательно)"
          name="contactEmail"
          value={editData?.contactEmail || ''}
          onChange={handleChange}
          type="email"
        />
        
        <NumericFormat
          customInput={TextField}
          fullWidth
          margin="normal"
          label="Контактный телефон"
          name="contactPhone"
          value={editData?.contactPhone || ''}
          onValueChange={(values) => {
            setEditData((prev) => ({ ...prev, contactPhone: values.value }));
          }}
          format="+7 (###) ###-##-##"
          mask="_"
          allowEmptyFormatting
          placeholder="+7 (___) ___-__-__"
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={editData?.is_active || false}
              onChange={handleChange}
              name="is_active"
              color="primary"
            />
          }
          label="Витрина активна"
          sx={{ mt: 2 }}
        />
        
        <Box mt={3} display="flex" gap={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={isCreating ? <AddIcon /> : <SaveIcon />}
            onClick={isCreating ? handleCreate : handleSave}
            disabled={saveLoading || !slugStatus.isUnique}>
            {saveLoading 
              ? 'Сохранение...' 
              : isCreating 
                ? 'Создать витрину' 
                : 'Сохранить'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={saveLoading}>
            Отмена
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Container className="p-4">
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 3,
          gap: 2,
        }}>
        <Typography variant="h6">Персональная витрина агента</Typography>
        {!isEditing && !isCreating && showcaseData && canManageShowcase(showcaseData) && (
          <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={handleEdit}>
            Редактировать
          </Button>
        )}
      </Box>
      
      {isEditing || isCreating ? (
        renderForm()
      ) : showcaseData ? (
        <Box mt={3}>
          <Paper elevation={2}>
            <Box p={3}>
              <Typography variant="h5">{showcaseData.title}</Typography>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1">{showcaseData.description}</Typography>
              
              <Box mt={3}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Контактная информация:
                </Typography>
				<Typography>Электронная почта: {showcaseData.contactEmail}</Typography>
                <Typography>Телефон: {formatPhoneNumber(showcaseData.contactPhone)}</Typography>
              </Box>
              
              <Box mt={2}>
                <Typography variant="subtitle2">
                  Статус:{' '}
                  {showcaseData.is_active ? (
                    <Box component="span" sx={{ color: 'success.main' }}>
                      Активна
                    </Box>
                  ) : (
                    <Box component="span" sx={{ color: 'warning.main' }}>
                      Не активна
                    </Box>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mt: 3 }}>
                  Ссылка на витрину:
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1,
                  }}>
                  <Box sx={{ wordBreak: 'break-all', fontWeight: 'bold' }}>
                    <a
                      href={baseShowcaseUrl + showcaseData.slug}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: '#1976d2' }}>
                      {baseShowcaseUrl}
                      <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {showcaseData.slug}
                      </Box>
                    </a>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: 'none', mt: { xs: 1, sm: 0 } }}
                    onClick={() =>
                      navigator.clipboard.writeText(baseShowcaseUrl + showcaseData.slug)
                    }>
                    скопировать ссылку
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      ) : (
        <Box mt={3}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" gutterBottom>
                У вас еще нет персональной витрины
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={3}>
                Создайте витрину, чтобы публиковать свои объекты недвижимости и получать больше клиентов
              </Typography>
              {canManageShowcase({ permissions: showcasePermissions }) && <Button
                variant="contained" 
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleStartCreate}
              >
                Создать витрину
              </Button>}
            </Box>
          </Paper>
        </Box>
      )}
      
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseNotification} severity={notification.type} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ShowcasePage;
